'use-client';
import React, {
  useMemo,
  useRef,
  useImperativeHandle,
  useState,
  forwardRef,
} from 'react';
import classNames from 'clsx';
import { formatClassNames } from '@wix/editor-elements-common-utils';
import semanticClassNames from '../FastGallery.semanticClassNames';
import type { IFastGalleryCompProps } from '../FastGallery.types';
import styles from './style/FastGallery.module.scss';
import { Gallery } from '@wix/fast-gallery-components';
import type {
  EventData,
  EventsListenerFunction,
  CustomGalleryRenderers,
} from '@wix/fast-gallery-library';
import { GALLERY_EVENTS } from '@wix/fast-gallery-library';
import {
  convertWixSDKItemsToContentItems,
  convertOrganizeMediaPanelItemsToContentItems,
} from './FastGallery.galleryUtils';
import { getResizeMediaUrl } from '@wix/photography-client-lib';
import { FastGalleryImageRenderer } from './FastGalleryImageRenderer';

const FastGallery: React.FC<IFastGalleryCompProps> = forwardRef(
  (props, ref) => {
    const {
      id,
      customClassNames = [],
      wixSDKItems,
      items = [],
      onItemClicked = () => {},
      onGetMoreItems = () => {},
      onCurrentItemChanged = () => {},
      itemClickAction,
      partToPreviewStateMap,
      shouldUseCustomImageRenderer,
    } = props;

    const resizeMediaUrlArgs = {
      imageToken: '',
      staticMediaUrls: {},
      isPrerenderMode: false,
      useOptimizedImageFormat: true,
    };
    const getResizedMediaSrc = getResizeMediaUrl(resizeMediaUrlArgs);
    const isSsr = typeof window === `undefined`;
    const windowRef = useRef<Window>(!isSsr ? window : null);
    const wrapperRef = !isSsr ? windowRef : undefined;

    const galleryItems = useMemo(
      () =>
        wixSDKItems?.length > 0
          ? convertWixSDKItemsToContentItems(wixSDKItems)
          : convertOrganizeMediaPanelItemsToContentItems(items),
      [wixSDKItems, items],
    );

    const [navigationApi, setNavigationApi] = useState<any>(undefined);

    const eventsListener: EventsListenerFunction = (
      eventName: GALLERY_EVENTS,
      eventData: EventData,
    ) => {
      switch (eventName) {
        case GALLERY_EVENTS.ON_ITEM_CLICKED:
          onItemClicked({
            itemIndex: eventData.index ?? -1,
            type: eventData.type,
          });
          break;
        case GALLERY_EVENTS.NEED_MORE_ITEMS:
          onGetMoreItems({
            from: eventData.index ?? -1,
          });
          break;
        case GALLERY_EVENTS.NAVIGATION_API_READY:
          setNavigationApi(eventData);
          break;
        case GALLERY_EVENTS.CURRENT_ITEM_CHANGED:
          onCurrentItemChanged({
            itemIndex: eventData.index ?? -1,
            type: eventData.type,
          });
          break;
        // TODO: add GALLERY_NAVIGATION_START event to fast-gallery-library
        // case GALLERY_EVENTS.GALLERY_NAVIGATION_START:
        //     onGalleryNavigationStart({
        //       animationDuration: eventData.scrollParams.durationInMS,
        //       continuousScroll: eventData.scrollParams.isContinuousScrolling,
        //       autoplay: !eventData.scrollParams.isManual,
        //       currentIndex: eventData.scrollParams.itemIdx,
        //       scrollDirection: eventData.scrollParams.scrollDirection,
        //       slideTransition: eventData.scrollParams.slideTransition,
        //     });
        //   ;
        //   break;
        default:
          break;
      }
    };

    const shouldShowHoverOnEdit =
      partToPreviewStateMap?.item === 'hover' ||
      partToPreviewStateMap?.image === 'hover' ||
      partToPreviewStateMap?.overlay === 'hover' ||
      partToPreviewStateMap?.title === 'hover' ||
      partToPreviewStateMap?.description === 'hover' ||
      partToPreviewStateMap?.counter === 'hover';

    useImperativeHandle(ref, () => ({
      next: () => {
        navigationApi.next();
      },
      previous: () => {
        navigationApi?.previous();
      },
      navigateNextEnabled: () => {
        return navigationApi?.canNavigateToNext();
      },
      navigatePreviousEnabled: () => {
        return navigationApi?.canNavigateToPrevious();
      },
      navigateToIndex: (itemIdx: Number, animationDuration?: Number) => {
        navigationApi?.navigateToIndex(itemIdx, animationDuration);
      },
      currentIndex: () => {
        return navigationApi?.currentIndex();
      },
      triggerItemClick: (itemIndex: number) => {
        return navigationApi?.triggerItemClick(undefined, { itemIndex });
      },
    }));
    const customRenderers: CustomGalleryRenderers = {};
    if (shouldUseCustomImageRenderer) {
      customRenderers.imageRenderer = FastGalleryImageRenderer;
    }
    return (
      <div
        id={id}
        className={classNames(
          'root',
          'fast-gallery',
          formatClassNames(semanticClassNames.root, ...customClassNames),
          styles.root,
          shouldShowHoverOnEdit || partToPreviewStateMap?.root === 'hover'
            ? styles.hoverOnEdit
            : '',
          shouldShowHoverOnEdit || partToPreviewStateMap?.root === 'hover'
            ? 'hover-preview'
            : '',
        )}
        data-preview={partToPreviewStateMap?.root}
      >
        <Gallery
          dataList={galleryItems.map(item => {
            return {
              ...item,
              url: item.mediaUrl,
            };
          })}
          options={{ itemClickAction }}
          eventsListener={eventsListener}
          createResizedMediaUrl={getResizedMediaSrc}
          customRenderers={customRenderers}
          wrapperRef={wrapperRef}
          selectors={{
            container: classNames(
              'container-reset',
              'container',
              formatClassNames(semanticClassNames.container),
              styles.container,
              styles.override,
            ),
            item: classNames(
              'item-reset',
              'item',
              formatClassNames(semanticClassNames.item),
              styles.item,
              shouldShowHoverOnEdit ? styles.hoverOnEdit : '',
              shouldShowHoverOnEdit ? 'hover-preview' : '',
            ),
            image: classNames(
              'image',
              formatClassNames(semanticClassNames.image),
              styles.image,
              shouldShowHoverOnEdit ? styles.hoverOnEdit : '',
              shouldShowHoverOnEdit ? 'hover-preview' : '',
            ),
            content: classNames(
              'content',
              formatClassNames(semanticClassNames.content),
              styles.content,
              shouldShowHoverOnEdit ? styles.hoverOnEdit : '',
              shouldShowHoverOnEdit ? 'hover-preview' : '',
            ),
            media: classNames(
              'media',
              formatClassNames(semanticClassNames.media),
              styles.media,
              shouldShowHoverOnEdit ? styles.hoverOnEdit : '',
              shouldShowHoverOnEdit ? 'hover-preview' : '',
            ),
            details: classNames(
              'details',
              formatClassNames(semanticClassNames.details),
              styles.details,
              shouldShowHoverOnEdit ? styles.hoverOnEdit : '',
              shouldShowHoverOnEdit ? 'hover-preview' : '',
            ),
            info: classNames('info'),
            title: classNames(
              'title',
              formatClassNames(semanticClassNames.title),
              styles.title,
              shouldShowHoverOnEdit ? styles.hoverOnEdit : '',
              shouldShowHoverOnEdit ? 'hover-preview' : '',
            ),
            description: classNames(
              'description',
              formatClassNames(semanticClassNames.description),
              styles.description,
              shouldShowHoverOnEdit ? styles.hoverOnEdit : '',
              shouldShowHoverOnEdit ? 'hover-preview' : '',
            ),
            overlay: classNames(
              'overlay',
              formatClassNames(semanticClassNames.overlay),
              styles.overlay,
              shouldShowHoverOnEdit ? styles.hoverOnEdit : '',
              shouldShowHoverOnEdit ? 'hover-preview' : '',
            ),
            counter: classNames(
              'counter',
              formatClassNames(semanticClassNames.counter),
              styles.counter,
              shouldShowHoverOnEdit ? styles.hoverOnEdit : '',
              shouldShowHoverOnEdit ? 'hover-preview' : '',
            ),
          }}
        />
      </div>
    );
  },
);

export default FastGallery;
